import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { A11y, Controller, EffectFade, Mousewheel, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { getContent, isDDEnv } from '@/utils/helper';
import { useIsKorean } from '@/utils/useLocale';
import {
  analytics,
  getCarouselDotNext,
  getClickCtaDetail,
  getClickExternalCtaDetail,
} from '@/utils/analytics';

import Pill from '@/atoms/Pill/Pill';
import Text from '@/atoms/Text/Text';
import { DynamicAsset } from '@/molecules/Media/index';
import CtfCta, { Cta } from '@/molecules/CTA/index';
import RichText from '@/molecules/RichText/RichText';

import {
  CtfAssetPositions,
  CtfCarouselStyle,
  CtfCtaStyle,
} from '@/utils/constants';
import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';

const SWIPER_MODULES = [A11y, Controller, Mousewheel, Pagination];

const SWIPER_PAGINATION = {
  clickable: true,
  el: '.aaco-carousel-featured__pagination',
  type: 'bullets',
};

const SWIPER_SPEED = 1000;

function CarouselAsymmetric({ assets = [], entries = [], fields = null, carouselContentStyle = {
    compactSpace: false,
    hasBackground: false,
    linkStyle: '',
  } }) {
  const paginationRef = useRef(null);
  const isKorean = useIsKorean();

  const [firstSwiper, setFirstSwiper] = useState(null);
  const [secondSwiper, setSecondSwiper] = useState(null);

  const {
    assetPosition,
    backgroundImage,
    isDarkBackground,
    cards,
    title,
    carouselStyle,
    removeYPadding,
    id,
  } = fields;

  const getCtaAnalytics = (e, data) => {
    analytics({
      window,
      document,
      detail: data?.localUrl
        ? getClickCtaDetail(e, data)
        : getClickExternalCtaDetail(e, data),
    });
  };

  let carouselCls = '';
  if (carouselStyle === CtfCarouselStyle.FEATURED_ASYMMETRIC_FULL)
    carouselCls = 'aaco-carousel-asymmetric--full-width';
  else if (carouselStyle === CtfCarouselStyle.FEATURED_ASYMMETRIC)
    carouselCls = 'aaco-carousel-asymmetric--full-padding';

  let assetPositionCls =
    assetPosition === CtfAssetPositions.LEFT
      ? 'aaco-carousel-asymmetric--left-image'
      : 'aaco-carousel-asymmetric--right-image';

  useEffect(() => {
    if (paginationRef?.current) {
      paginationRef.current
        ?.querySelectorAll('.swiper-pagination-bullet')
        .forEach(function (bullet) {
          bullet.addEventListener('click', function (el) {
            analytics({
              window,
              document,
              detail: getCarouselDotNext(el),
            });
          });
        });
    }
  }, [paginationRef]);

  const hasBackground = backgroundImage?.[0]?.original_secure_url;

  return (
    <div
      id={id}
      className={`aaco-carousel-asymmetric overflow-hidden
      ${carouselCls} ${assetPositionCls} ${
        !carouselContentStyle.compactSpace && !removeYPadding && 'pb-20'
      }`}
      aria-label={title}
    >
      <div
        className={`${
          carouselStyle === CtfCarouselStyle.FEATURED_ASYMMETRIC_FULL
            ? ''
            : 'container mx-auto'
        }`}
      >
        <div className="relative">
          <div
            className={`aaco-carousel__swiper dd-kr:order-1 dd-en:order-1 drop-shadow-2xl z-10`}
          >
            <Swiper
              controller={{ control: secondSwiper }}
              direction="horizontal"
              modules={SWIPER_MODULES}
              mousewheel={{
                forceToAxis: true,
              }}
              onSwiper={setFirstSwiper}
              speed={SWIPER_SPEED}
            >
              {cards.map((card, index) => {
                const content = getContent(card.sys?.id, entries);
                const { dynamicAsset, cloudinaryAssets, title } =
                  content.fields;
                return (
                  <SwiperSlide key={index}>
                    <div className="aaco-carousel-card__slide">
                      {(cloudinaryAssets || dynamicAsset) && (
                        <div className="aaco-card__image">
                          <DynamicAsset
                            assets={assets}
                            entries={entries}
                            field={cloudinaryAssets ?? dynamicAsset}
                            style={`${
                              carouselStyle ===
                              CtfCarouselStyle.FEATURED_ASYMMETRIC_FULL
                                ? ''
                                : 'dd-en:overflow-hidden dd-kr:overflow-hidden dd-en:md:rounded-none dd-kr:md:rounded-none'
                            }`}
                            container={
                              carouselStyle === CtfCarouselStyle.FEATURED_FULL
                                ? 'half'
                                : 'half-container'
                            }
                            title={title}
                          />
                        </div>
                      )}
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
          <div className="aaco-carousel__content relative z-0 flex flex-col min-h-full py-9 items-center justify-between dd-kr:justify-center dd-en:justify-center overflow-hidden dd-en:pt-[150px] dd-kr:pt-[150px] md:dd-en:py-[112px] md:dd-kr:py-[112px]">
            <div
              className={`bg-cover absolute bg-center w-full h-full rounded-[8px] md:dd-en:rounded-l-none md:dd-kr:rounded-l-none md:dd-en:mr-[4rem] md:dd-kr:mr-[4rem] ${
                carouselStyle === CtfCarouselStyle.FEATURED_ASYMMETRIC_FULL
                  ? 'rounded-t-none'
                  : ''
              }`}
              style={{
                backgroundImage:
                  hasBackground &&
                  `url(${backgroundImage?.[0]?.original_secure_url})`,
              }}
            ></div>
            <Swiper
              className="w-full min-h-full flex flex-col justify-between flex-1"
              controller={{ control: firstSwiper }}
              effect="fade"
              fadeEffect={{ crossFade: true }}
              modules={[...SWIPER_MODULES, EffectFade]}
              onSwiper={setSecondSwiper}
              pagination={SWIPER_PAGINATION}
              speed={SWIPER_SPEED}
            >
              {cards.map((card, index) => {
                const content = getContent(card.sys?.id, entries);
                let {
                  primaryCTA,
                  title,
                  text,
                  tags = [],
                  subtitle,
                } = content.fields;
                text =
                  text && text.sys
                    ? getContent(text.sys.id, entries)?.fields?.text
                    : text;

                const tag =
                  tags.length > 0 ? getContent(tags[0].sys?.id, entries) : null;
                return (
                  <SwiperSlide key={index}>
                    <div className="container mx-auto content relative flex-1">
                      <div className="md:dd-kr:w-1/2 md:dd-en:w-1/2 flex flex-col justify-between items-start min-h-full md:dd-kr:pr-20 md:dd-en:pr-20">
                        {tag && (
                          <Pill
                            style="pill-action dd-en:mb-0 dd-kr:mb-0 mb-4 inline-flex items-center"
                            onClick={() => {/* Search disabled */}}
                            small
                          >
                            {tag.fields?.name}
                          </Pill>
                        )}
                        {subtitle && (
                          <Text
                            tag="tag"
                            style={`${
                              isDarkBackground ? 'text-white' : 'text-grey-6'
                            } ${tag ? 'dd-en:pt-[25px] dd-kr:pt-[25px]' : ''} ${
                              !isDDEnv() && ' py-9'
                            }`}
                          >
                            {subtitle}
                          </Text>
                        )}
                        <div
                          className={`flex-1 flex flex-col justify-center items-start dd-en:mb-0 dd-kr:mb-0 mb-4 ${
                            !isDDEnv() && 'py-4 '
                          }`}
                        >
                          <h2
                            className={`text-h4 font-normal dd-kr:text-h4-mobile dd-en:text-h4-mobile md:dd-kr:text-h4 md:dd-en:text-h4 mb-5 dd-en:mb-0 dd-kr:mb-0 dd-en:pt-5 dd-kr:pt-5 font-heading ${
                              isDarkBackground
                                ? 'text-actual-dd-gold'
                                : 'text-progressive-blue'
                            }`}
                          >
                            {title}
                          </h2>
                          {text && !text.content && (
                            <p
                              className={`text-white font-text font-body text-body-small dd-kr:text-body dd-en:text-body whitespace-pre-line ${
                                !isDDEnv() && 'mb-5'
                              } dd-en:text-body-text-grey dd-kr:text-body-text-grey dd-en:mt-[15px] dd-kr:mt-[15px]`}
                            >
                              {text}
                            </p>
                          )}
                          {text &&
                            text.content &&
                            text.content.length > 0 &&
                            text.content.map(({ nodeType, content }, index) => (
                              <RichText
                                entries={entries}
                                nodeType={nodeType}
                                content={content}
                                key={index}
                                richTextStyle={{
                                  paragraph: {
                                    customFont:
                                      TEXT_BASE_CLASS['body'] +
                                      (carouselContentStyle.hasBackground ||
                                      isDarkBackground
                                        ? ' text-majesty-50 dd-kr:text-80-white-on-dark-blue dd-en:text-80-white-on-dark-blue'
                                        : '') +
                                      ' font-text text-body',
                                    blockquoteFont:
                                      TEXT_BASE_CLASS['blockquote'],
                                  },
                                }}
                              />
                            ))}
                          {primaryCTA && primaryCTA.sys && (
                            <div className="dd-en:mt-5 dd-kr:mt-5">
                              <CtfCta
                                field={primaryCTA}
                                entries={entries}
                                invert={isDarkBackground}
                                onClick={getCtaAnalytics}
                              />
                            </div>
                          )}
                          {primaryCTA && !primaryCTA.sys && (
                            <div className="aaco-carousel-hero__cta mt-4 dd-en:mt-5 dd-kr:mt-5">
                              <Cta
                                buttonProps={{
                                  invert: isDarkBackground,
                                  name:
                                    isKorean
                                      ? '자세히 보기'
                                      : 'Learn more',
                                  type: isDDEnv()
                                    ? CtfCtaStyle.DD_SECONDARY
                                    : CtfCtaStyle.SECONDARY,
                                  style: 'w-fit',
                                }}
                                iconProps={{
                                  name: isDarkBackground
                                    ? 'arrow-right'
                                    : 'external-link',
                                  height: isDarkBackground ? 15 : 18,
                                  width: isDarkBackground ? 15 : 18,
                                  style: isDarkBackground
                                    ? 'text-violet-bright'
                                    : 'text-actual-dd-gold',
                                  hasIconBorder: isDarkBackground,
                                }}
                                linkProps={{
                                  url: primaryCTA,
                                }}
                                onClick={getCtaAnalytics}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
              <div className="swiper-control container mx-auto">
                <div
                  className="aaco-carousel-featured__pagination flex items-center dd-en:mt-10 dd-kr:mt-10 md:dd-en:mt-[60px] md:dd-kr:mt-[60px]"
                  ref={paginationRef}
                ></div>
              </div>
            </Swiper>
          </div>
        </div>
      </div>
    </div>
  );
}

CarouselAsymmetric.propTypes = {
  assets: PropTypes.array,
  carouselContentStyle: PropTypes.object,
  entries: PropTypes.array,
  fields: PropTypes.object,
};


export default CarouselAsymmetric;
